import React from "react";
import { Trash2 } from "lucide-react";
import { CreatePOProductColumnHeader } from "src/enums";
import { TableColumn, CreatePOProductInputField } from "src/types";
import { TextInputBox } from "src/components";
import { Table } from "src/components";

interface CreatePOProductInputTableProps {
  products: CreatePOProductInputField[];
  setProducts: React.Dispatch<React.SetStateAction<CreatePOProductInputField[]>>;
}

const CreatePOProductInputTable: React.FC<CreatePOProductInputTableProps> = ({ products, setProducts }) => {
  const handleProductChange = <T,>(fieldId: string, fieldName: keyof CreatePOProductInputField, value: T) => {
    setProducts(
      products.map((product) => (product.fieldId === fieldId ? { ...product, [fieldName]: value } : product))
    );
  };

  const createPOProductTableColumns: TableColumn<CreatePOProductColumnHeader, CreatePOProductInputField>[] = [
    {
      header: CreatePOProductColumnHeader.PRODUCT_ID,
      column: (row) => (
        <div className="min-w-[200px]">
          <TextInputBox
            id={`product-id-${row.fieldId}`}
            value={(products.find((product) => product.fieldId === row.fieldId)?.productId ?? "").toString()}
            onChange={(value) => handleProductChange(row.fieldId, "productId", value)}
            placeholder="Enter 6-15 digit ID"
            maxLength={15}
            size="sm"
          />
        </div>
      ),
      styles: { minWidth: "220px" }
    },
    {
      header: CreatePOProductColumnHeader.UK_QTY,
      column: (row) => (
        <div className="w-28">
          <TextInputBox
            id={`uk-qty-${row.fieldId}`}
            value={(products.find((product) => product.fieldId === row.fieldId)?.ukQuantity ?? "").toString()}
            onChange={(value) => handleProductChange(row.fieldId, "ukQuantity", value)}
            placeholder="UK"
            maxLength={10}
            size="sm"
          />
        </div>
      ),
      styles: { minWidth: "120px" }
    },
    {
      header: CreatePOProductColumnHeader.EU_QTY,
      column: (row) => (
        <div className="w-28">
          <TextInputBox
            id={`eu-qty-${row.fieldId}`}
            value={(products.find((product) => product.fieldId === row.fieldId)?.euQuantity ?? "").toString()}
            onChange={(value) => handleProductChange(row.fieldId, "euQuantity", value)}
            placeholder="EU"
            maxLength={10}
            size="sm"
          />
        </div>
      ),
      styles: { minWidth: "120px" }
    },
    {
      header: CreatePOProductColumnHeader.EXTRA_INFO,
      column: (row) => (
        <div className="min-w-[250px]">
          <TextInputBox
            id={`extra-info-${row.fieldId}`}
            value={(products.find((product) => product.fieldId === row.fieldId)?.extraInfo ?? "").toString()}
            onChange={(value) => handleProductChange(row.fieldId, "extraInfo", value)}
            placeholder="Optional notes..."
            maxLength={200}
            size="sm"
          />
        </div>
      ),
      styles: { minWidth: "260px" }
    },
    {
      header: CreatePOProductColumnHeader.ACTIONS,
      column: (row) => (
        <button
          onClick={() => setProducts(products.filter((product) => product.fieldId !== row.fieldId))}
          disabled={products.length === 1}
          className="group flex items-center justify-center p-2 rounded-md hover:bg-error-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
          title="Delete product"
          type="button"
        >
          <Trash2 className="h-4 w-4 text-error-600 group-hover:text-error-700 transition-colors" />
        </button>
      ),
      styles: { minWidth: "60px", textAlign: "center" }
    }
  ];

  return (
    <Table<CreatePOProductColumnHeader, CreatePOProductInputField>
      data={products}
      columns={createPOProductTableColumns}
    />
  );
};

export { CreatePOProductInputTable };
